// Recarga automática de créditos por marca (ADR-2026-10-29-recarga-automatica-de-marca). Cuando el
// saldo de una marca baja del umbral, el servidor de pagos cobra un pack con la tarjeta guardada de la
// agencia. Los packs, los umbrales y el precio son los de atlas-payment-server
// (`src/domain/recargas.ts`): si cambian allá, cambian acá.

export const PRECIO_POR_CREDITO_ARS = 220;
export const PACKS_DE_RECARGA = [50, 100, 250, 500] as const;
/** Pack chico para probar con plata real: sólo cuentas `dev_tester`. */
export const PACK_DE_PRUEBA = 5;
export const UMBRALES_DE_RECARGA = [5, 10, 20, 50] as const;
export const TOPE_DE_RECARGAS_POR_MES = 10;
/** Los créditos de cada recarga vencen a los N días de acreditarse (ADR-2026-10-29-vencimiento-de-recargas). */
export const VIGENCIA_DE_RECARGA_DIAS = 90;

export type MotivoDePausa = 'tarjeta_rechazada' | 'sin_tarjeta' | 'cobro_incierto';

export interface RecargaDeMarca {
  activa: boolean;
  creditos: number;
  umbral: number;
  /** Por qué la apagó el servidor; null si está activada o la desactivó el dueño. */
  motivoPausa: MotivoDePausa | null;
  /** Recargas de este mes (sólo las que trae /marca/estado; la pantalla de "lista" no las sabe). */
  recargasDelMes?: number;
  topeMensual?: number;
  /** El próximo lote de recarga por vencer (sólo lo trae /marca/estado). null = no hay créditos por vencer. */
  vencimiento?: { creditos: number; fecha: string } | null;
  /**
   * El plan de la marca tiene la baja agendada: el servidor no recarga más, aunque la recarga haya
   * quedado activada. Se muestra desactivada y no se puede configurar.
   */
  detenidaPorBaja?: boolean;
  /** La baja la pidió el dueño ("Cancelar"); si no, vino por otro lado (morosidad). */
  bajaPedidaPorElDueno?: boolean;
}

export const RECARGA_POR_DEFECTO: RecargaDeMarca = { activa: true, creditos: 100, umbral: 10, motivoPausa: null };

export function packsDisponibles(devTester: boolean): number[] {
  return devTester ? [PACK_DE_PRUEBA, ...PACKS_DE_RECARGA] : [...PACKS_DE_RECARGA];
}

export const precioDePack = (creditos: number): number => creditos * PRECIO_POR_CREDITO_ARS;

export const pesos = (n: number): string => `$${n.toLocaleString('es-AR')}`;

const creditosEnTexto = (n: number) => `${n.toLocaleString('es-AR')} ${n === 1 ? 'crédito' : 'créditos'}`;

/** "100 créditos ($22.000)". */
export const packEnTexto = (creditos: number): string => `${creditosEnTexto(creditos)} (${pesos(precioDePack(creditos))})`;

/** La recarga tal como se muestra si el plan de la marca tiene (o no) la baja agendada. */
export function recargaConBaja(
  r: RecargaDeMarca | null,
  baja: boolean,
  pedidaPorElDueno = false,
): RecargaDeMarca | null {
  return r && baja ? { ...r, detenidaPorBaja: true, bajaPedidaPorElDueno: pedidaPorElDueno } : r;
}

/** El título del recuadro. */
export const tituloDeLaRecarga = (r: RecargaDeMarca): string =>
  r.activa && !r.detenidaPorBaja ? 'Recarga automática activada' : 'Recarga automática desactivada';

/** La bajada del recuadro: qué hace, con los números. */
export function detalleDeLaRecarga(r: RecargaDeMarca): string {
  if (r.detenidaPorBaja) {
    return r.bajaPedidaPorElDueno
      ? 'Se detuvo porque cancelaste el plan de esta marca.'
      : 'Se detuvo porque el plan de esta marca se va a dar de baja.';
  }
  if (!r.activa) return 'Activala para no quedarte sin créditos en el medio de un trabajo.';
  return `Cuando le queden menos de ${creditosEnTexto(r.umbral)}, recarga automáticamente ${packEnTexto(r.creditos)}.`;
}

/** "40 créditos vencen el 27 de enero." (con el año si no es el de hoy). null si no hay nada por vencer. */
export function vencimientoEnTexto(r: RecargaDeMarca, ahora: Date = new Date()): string | null {
  const v = r.vencimiento;
  if (!v || !(v.creditos > 0)) return null;
  const fecha = new Date(v.fecha);
  if (Number.isNaN(fecha.getTime())) return null;
  const zona = 'America/Argentina/Buenos_Aires';
  const anio = (d: Date) => d.toLocaleDateString('es-AR', { year: 'numeric', timeZone: zona });
  const dia = fecha.toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    ...(anio(fecha) !== anio(ahora) ? { year: 'numeric' } : {}),
    timeZone: zona,
  });
  const n = Math.floor(v.creditos);
  return `${creditosEnTexto(n)} ${n === 1 ? 'vence' : 'vencen'} el ${dia}.`;
}

/** El aviso de la marca cuando la recarga no va a salir. null = nada que avisar. */
export function avisoDeLaRecarga(r: RecargaDeMarca): string | null {
  if (r.detenidaPorBaja) return null;
  switch (r.motivoPausa) {
    case 'tarjeta_rechazada':
      return 'La tarjeta rechazó la última recarga y la desactivamos. Revisá la tarjeta y volvé a activarla.';
    case 'sin_tarjeta':
      return 'No pudimos cobrar la recarga con la tarjeta guardada y la desactivamos. Revisá la tarjeta y volvé a activarla.';
    case 'cobro_incierto':
      return 'No pudimos confirmar el cobro de la última recarga y la pausamos. Si el pago entró, los créditos se suman y la recarga se vuelve a activar sola.';
    default:
      break;
  }
  if (r.activa && topeDelMesAlcanzado(r)) {
    const tope = r.topeMensual ?? TOPE_DE_RECARGAS_POR_MES;
    return `Esta marca ya hizo las ${tope} recargas de este mes. Vuelve a recargar el mes que viene.`;
  }
  return null;
}

/** La marca ya hizo todas las recargas del mes. Sin el conteo (lectura por RLS) no se sabe: false. */
export function topeDelMesAlcanzado(r: RecargaDeMarca): boolean {
  return r.recargasDelMes != null && r.recargasDelMes >= (r.topeMensual ?? TOPE_DE_RECARGAS_POR_MES);
}

/** La recarga que trae /marca/estado (`marcas[].recarga`), en el shape de la pantalla. */
export function recargaDeEstado(
  e:
    | {
        activa: boolean;
        creditos: number;
        umbral: number;
        motivo_pausa: string | null;
        recargas_del_mes: number;
        tope_mensual: number;
        vencimiento?: { creditos: number; fecha: string } | null;
      }
    | undefined,
): RecargaDeMarca | null {
  if (!e) return null;
  return {
    activa: e.activa,
    creditos: e.creditos,
    umbral: e.umbral,
    motivoPausa: esMotivo(e.motivo_pausa) ? e.motivo_pausa : null,
    recargasDelMes: e.recargas_del_mes,
    topeMensual: e.tope_mensual,
    vencimiento: e.vencimiento ?? null,
  };
}

/** La recarga leída de la billetera por RLS (la pantalla de "lista"). */
export function recargaDeBilletera(b: {
  recarga_activa?: boolean | null;
  recarga_creditos?: number | null;
  recarga_umbral?: number | null;
  recarga_pausada_motivo?: string | null;
}): RecargaDeMarca {
  return {
    activa: b.recarga_activa === true,
    creditos: Number(b.recarga_creditos ?? RECARGA_POR_DEFECTO.creditos),
    umbral: Number(b.recarga_umbral ?? RECARGA_POR_DEFECTO.umbral),
    motivoPausa: esMotivo(b.recarga_pausada_motivo) ? b.recarga_pausada_motivo : null,
  };
}

function esMotivo(v: unknown): v is MotivoDePausa {
  return v === 'tarjeta_rechazada' || v === 'sin_tarjeta' || v === 'cobro_incierto';
}

/** El texto de un rechazo de POST /marca/recarga. */
export function mensajeDeErrorDeRecarga(error: string): string {
  switch (error) {
    case 'no_es_el_dueno':
      return 'Sólo el dueño de la cuenta puede cambiar la recarga automática.';
    case 'pack_no_permitido':
    case 'umbral_no_permitido':
      return 'Esa opción no está disponible. Elegí otra.';
    case 'sin_billetera':
    case 'sin_plan':
      return 'Esta marca todavía no tiene plan. Contratá uno para poder recargarla.';
    default:
      return 'No pudimos guardar la recarga automática. Probá de nuevo.';
  }
}
